import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { get, post, put, deleteRequestV2 } from '../../api/usersModule/httpClient';
import { useDebounce } from '../../hooks/useDebounce';
import { LargeCard } from '../Cards/LargeCard';
import { CDBModal } from "cdbreact";
import { ModalArticleTypes } from "../Modals/ModalArticleTypes";

const usersHeaders = ['ID', 'Nombre', "/article-types"];

const defaultFormData = {
  name: ''
};

export const CrudArticleTypes = ({ userName, permissions, project }) => {
  const [data, setData] = useState([]);
  const [state, setState] = useState({ modal: false });
  const [page, setPage] = useState(1);
  const [pressedButton, setPressedButton] = useState('');
  const [action, setAction] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const [totalItems, setTotalItems] = useState(0);
  const [initialRender, setInitialRender] = useState(true);
  const [changePage, setChangePage] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  const [formData, setFormData] = useState({
    ...defaultFormData,
    user: userName,
  });

  const fetchArticleTypes = async (search, statusPage) => {
    setLoading(true);
    let response;
    try {
      if (search !== '') {
        response = await get(`/article-types?page=${statusPage}&search=${search}&limit=${itemsPerPage}`);
      } else {
        response = await get(`/article-types?page=${statusPage}&limit=${itemsPerPage}`);
      }
      setTotalItems(response.answer ? response.pagination.totalItems : 0);
      
      // Sanitizar y convertir datos al formato que espera la tabla
      const sanitizedData = response.answer && response.data ? 
        response.data.map(item => [
          String(item.id || ''),
          String(item.name || '')
        ]) : [];
      
      setData(sanitizedData);
    } catch (error) {
      console.error('Error fetching article types:', error);
      setData([]);
      setTotalItems(0);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!initialRender || changePage) {
      fetchArticleTypes(debouncedSearch, page);
      setChangePage(false);
    }
  }, [debouncedSearch, page, changePage, itemsPerPage]);

  useEffect(() => {
    if (initialRender) {
      fetchArticleTypes('', 1);
      setInitialRender(false);
    }
  }, [initialRender]);

  useEffect(() => {
    switch (pressedButton.split(',')[0]) {
      case "create":
        openCreateModal();
        break;
      case "edit":
        // El ID viene en pressedButton como "edit,ID"
        const articleTypeId = pressedButton.split(',')[1];
        const articleTypeArray = data.find(item => item[0] == articleTypeId);
        if (articleTypeArray) {
          // Convertir array a objeto para el modal
          const articleType = {
            id: articleTypeArray[0],
            name: articleTypeArray[1]
          };
          openEditModal(articleType);
        }
        break;
      case "refresh":
        fetchArticleTypes(debouncedSearch, page);
        break;
      case "closeModal":
        setState({ modal: false });
        break;
      case "submitModal":
        handleSubmit();
        break;
      case "delete":
        if (formData.id) {
          handleDelete(formData.id);
        }
        break;
      default:
        break;
    }
    setPressedButton("");
  }, [pressedButton, debouncedSearch, page, data]);

  const handleCreate = async () => {
    Swal.fire({
      title: 'Creando Tipo de Artículo',
      text: 'Por favor, espere...',
      icon: 'info',
      allowOutsideClick: false,
      showCancelButton: false,
      showConfirmButton: false,
      willOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const response = await post('/article-types', formData);
      Swal.close();
      
      if (response.answer) {
        fetchArticleTypes(debouncedSearch, page);
        setState({ modal: false });
        setFormData({ ...defaultFormData, user: userName });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error al crear el tipo de artículo',
          text: response.message || 'Error desconocido'
        });
      }
    } catch (error) {
      Swal.close();
      console.error('Error creating article type:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error al crear el tipo de artículo',
        text: 'Error de conexión'
      });
    }
  };

  const handleUpdate = async () => {
    Swal.fire({
      title: 'Actualizando Tipo de Artículo',
      text: 'Por favor, espere...',
      icon: 'info',
      allowOutsideClick: false,
      showCancelButton: false,
      showConfirmButton: false,
      willOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const response = await put(`/article-types`, formData);
      Swal.close();
      
      if (response.answer) {
        fetchArticleTypes(debouncedSearch, page);
        setState({ modal: false });
        setFormData({ ...defaultFormData, user: userName });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error al actualizar el tipo de artículo',
          text: response.message || 'Error desconocido'
        });
      }
    } catch (error) {
      Swal.close();
      console.error('Error updating article type:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error al actualizar el tipo de artículo',
        text: 'Error de conexión'
      });
    }
  };

  const handleDelete = async (id) => {
    const confirmDelete = await Swal.fire({
      title: 'Confirmar Eliminación del Tipo de Artículo',
      text: 'Esta acción eliminará de manera permanente el Tipo de Artículo seleccionado. ¿Está seguro de que desea proceder?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#146284',
      cancelButtonColor: '#EF4444',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    });

    if (confirmDelete.isConfirmed) {
      Swal.fire({
        title: 'Eliminando Tipo de Artículo',
        text: 'Por favor, espere...',
        icon: 'info',
        allowOutsideClick: false,
        showCancelButton: false,
        showConfirmButton: false,
        willOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        const response = await deleteRequestV2(`/article-types`, JSON.stringify({ id }));
        Swal.close();
        
        if (response.answer) {
          Swal.fire({
            icon: 'success',
            title: 'Tipo de artículo eliminado exitosamente',
            showConfirmButton: false,
            timer: 1500
          });
          fetchArticleTypes(debouncedSearch, page);
          setState({ modal: false });
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error al eliminar el tipo de artículo',
            text: response.message || 'Error desconocido'
          });
        }
      } catch (error) {
        Swal.close();
        console.error('Error deleting article type:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error al eliminar el tipo de artículo',
          text: 'Error de conexión'
        });
      }
    }
  };

  const openCreateModal = () => {
    setAction('create');
    setFormData({ ...defaultFormData, user: userName });
    setState({ modal: true });
  };

  const openEditModal = (articleType) => {
    setAction('edit');
    setFormData({ ...articleType, user: userName });
    setState({ modal: true });
  };

  const handleSubmit = () => {
    if (action === 'create') {
      handleCreate();
    } else if (action === 'edit') {
      handleUpdate();
    }
  };

  return (
    <div>
      <LargeCard
        name="Gestión de Tipos de Artículos"
        headers={usersHeaders}
        data={data}
        loading={loading}
        search={search}
        setSearch={setSearch}
        page={page}
        setPage={setPage}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
        setChangePage={setChangePage}
        setPressedButton={setPressedButton}
        cState={true}
      />
      
      <CDBModal
        isOpen={state.modal}
        toggle={() => setState({ modal: !state.modal })}
        size="lg"
      >
        <ModalArticleTypes
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleSubmit}
          onCancel={() => setState({ modal: false })}
          action={action}
          setPressedButton={setPressedButton}
        />
      </CDBModal>
    </div>
  );
};